// ---------------------------------------------------------------------------
// useLiveStream — SP-5f Task B2
// Orchestrates: snapshot fetch → SSE EventSource → reconnect with back-off.
// ---------------------------------------------------------------------------

import { useReducer, useEffect, useRef, useState, useCallback } from 'react';
import { useLiveSnapshot, useRequestLiveTicket, type SnapshotDto } from '../api/live';
import {
  liveReducer,
  initialLiveState,
  type LiveState,
  type LiveAction,
  type SnapshotData,
  type Session,
  type PickListLite,
} from './liveReducer';

export type ConnectionStatus =
  | 'idle'
  | 'connecting'
  | 'snapshot'
  | 'live'
  | 'reconnecting'
  | 'disconnected';

export interface UseLiveStreamArgs {
  token: string | null;
  tenantCode: string | null;
  warehouseId: number | null;
  enabled: boolean;
}

export interface UseLiveStreamReturn {
  state: LiveState;
  status: ConnectionStatus;
  reconnect: () => void;
}

const KNOWN_EVENTS = [
  'session.started',
  'session.ended',
  'fsm.transition',
  'task.opened',
  'task.confirmed',
  'task.skipped',
  'task.forced',
  'mute.changed',
  'pause.changed',
  'pick_list.imported',
  'pick_list.assigned',
  'pick_list.confirmed',
  'pick_list.cancelled',
  'mission.state_changed',
] as const;

/** Traefik routes /sse/* to the SSE gateway (SP-4b). */
const SSE_BASE = '/sse';

// ---------------------------------------------------------------------------
// DTO → domain mapping
// ---------------------------------------------------------------------------

/**
 * Maps a snake_case SnapshotDto (from the API) to the camelCase SnapshotData
 * expected by the reducer's 'init' action.
 */
function mapSnapshotDto(dto: SnapshotDto): SnapshotData {
  const sessions: Session[] = dto.sessions.map((s) => ({
    sessionId: s.session_id,
    operatorUserId: s.operator_user_id,
    operatorDisplayName: s.operator_display_name,
    missionId: s.mission_id,
    pickListId: s.pick_list_id,
    pickListExternalId: s.pick_list_external_id,
    pickListRecipient: s.pick_list_recipient,
    currentTaskId: s.current_task_id,
    currentTaskLocation: s.current_task_location,
    currentTaskItemName: s.current_task_item_name,
    qtyRequired: s.qty_required,
    qtyPicked: s.qty_picked,
    fsmState: s.fsm_state,
    isMuted: s.is_muted,
    isPaused: s.is_paused,
    startedAt: s.started_at,
    lastEventAt: s.last_event_at,
    tasksDone: s.tasks_done,
    tasksTotal: s.tasks_total,
  }));

  const pickListsInProgress: PickListLite[] = dto.pick_lists_in_progress.map((pl) => ({
    pickListId: pl.pick_list_id,
    externalId: pl.external_id,
    recipient: pl.recipient,
    status: pl.status,
    totalMissions: pl.total_missions,
    activeMissions: pl.active_missions,
  }));

  return {
    warehouseId: dto.warehouse_id,
    snapshotAt: dto.snapshot_at,
    sessions,
    pickListsInProgress,
  };
}

/** Maximum reconnect back-off delay in milliseconds. */
const MAX_BACKOFF_MS = 8_000;

export function useLiveStream({
  token,
  tenantCode,
  warehouseId,
  enabled,
}: UseLiveStreamArgs): UseLiveStreamReturn {
  const [state, dispatch] = useReducer(liveReducer, initialLiveState);
  const [status, setStatus] = useState<ConnectionStatus>('idle');

  const esRef = useRef<EventSource | null>(null);
  const retryRef = useRef<{ attempt: number; timeoutId: number | null }>({
    attempt: 0,
    timeoutId: null,
  });
  // Tracks the last event id seen so reconnects can send Last-Event-ID.
  // Stored in a ref so the EventSource callback captures the latest value
  // without re-creating the listener on every render.
  const lastEventAtRef = useRef<string | null>(null);

  const snapshotQuery = useLiveSnapshot(token, tenantCode, enabled ? warehouseId : null);
  const ticketMut = useRequestLiveTicket(token, tenantCode);

  // Keep ref in sync with reducer state after each SSE event.
  useEffect(() => {
    if (state.lastEventAt) {
      lastEventAtRef.current = state.lastEventAt;
    }
  }, [state.lastEventAt]);

  const closeEventSource = useCallback(() => {
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
  }, []);

  // Forward-declare so scheduleReconnect and openStream can reference each other.
  // We use refs to break the circular dependency without adding them to
  // dependency arrays (which would cause infinite re-registration loops).
  const openStreamRef = useRef<() => void>(() => undefined);
  const scheduleReconnectRef = useRef<() => void>(() => undefined);

  const openStream = useCallback(() => {
    if (!enabled || !warehouseId) return;

    const currentAttempt = retryRef.current.attempt;
    setStatus(currentAttempt > 0 ? 'reconnecting' : 'connecting');

    ticketMut
      .mutateAsync({ warehouseId })
      .then((ticket) => {
        const params = new URLSearchParams({
          ticket: ticket.ticket,
          warehouse_id: String(warehouseId),
        });
        if (lastEventAtRef.current) {
          params.set('last_event_id', lastEventAtRef.current);
        }

        closeEventSource();
        const es = new EventSource(`${SSE_BASE}/live/stream?${params.toString()}`);
        esRef.current = es;

        KNOWN_EVENTS.forEach((evType) => {
          es.addEventListener(evType, (rawEv: Event) => {
            const ev = rawEv as MessageEvent;
            let data: Record<string, unknown> = {};
            try {
              data = JSON.parse(ev.data as string) as Record<string, unknown>;
            } catch {
              // Ignore malformed SSE payloads.
            }
            const action: LiveAction = {
              type: 'sse',
              id: ev.lastEventId ?? '',
              event: evType,
              data,
            };
            dispatch(action);
          });
        });

        es.onopen = () => {
          setStatus('live');
          retryRef.current.attempt = 0;
        };

        es.onerror = () => {
          closeEventSource();
          setStatus('disconnected');
          scheduleReconnectRef.current();
        };
      })
      .catch(() => {
        setStatus('disconnected');
        scheduleReconnectRef.current();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, warehouseId, closeEventSource]);
  // ticketMut intentionally omitted — mutations are stable per render cycle
  // and including the mutation object would cause re-registration loops.

  // Store in ref so onerror callback always calls the latest version.
  openStreamRef.current = openStream;

  const scheduleReconnect = useCallback(() => {
    retryRef.current.attempt += 1;
    const attempt = retryRef.current.attempt;
    const delay = Math.min(MAX_BACKOFF_MS, 1_000 * 2 ** (attempt - 1));
    retryRef.current.timeoutId = window.setTimeout(() => {
      retryRef.current.timeoutId = null;
      openStreamRef.current();
    }, delay);
  }, []);

  scheduleReconnectRef.current = scheduleReconnect;

  // Apply snapshot on first load, then open the SSE stream.
  useEffect(() => {
    if (!snapshotQuery.data) return;

    dispatch({ type: 'init', snapshot: mapSnapshotDto(snapshotQuery.data) });
    setStatus('snapshot');
    // Open stream after snapshot is applied.
    openStreamRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotQuery.data]);

  // Clean up when disabled.
  useEffect(() => {
    if (!enabled) {
      closeEventSource();
      if (retryRef.current.timeoutId !== null) {
        clearTimeout(retryRef.current.timeoutId);
        retryRef.current.timeoutId = null;
      }
      setStatus('idle');
    }
  }, [enabled, closeEventSource]);

  // Teardown on unmount.
  useEffect(() => {
    return () => {
      closeEventSource();
      if (retryRef.current.timeoutId !== null) {
        clearTimeout(retryRef.current.timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeEventSource]);

  const reconnect = useCallback(() => {
    closeEventSource();
    if (retryRef.current.timeoutId !== null) {
      clearTimeout(retryRef.current.timeoutId);
      retryRef.current.timeoutId = null;
    }
    retryRef.current.attempt = 0;
    openStreamRef.current();
  }, [closeEventSource]);

  return { state, status, reconnect };
}
